import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { type GroupDefinitionMap } from '~/components/features/GroupCountsFigures'
import { useSurveyQueryActiveBrandGroup } from '~/hooks/useSurveyQuery'
import { type Group } from '~/utils'

import { lastTabulatedVariablesAtom } from '../atoms'
import {
  createFiveSegmentWithAllLabelMap,
  invertedOverlapKeyMap,
  type OverlapKey,
} from '../utils'

// 1グループを構成するセルの区切り。グループは選択セルの和集合。
const CELL_SEPARATOR = ' + '
// 1セル内のブランド1側 × ブランド2側の区切り。
const BRAND_SEPARATOR = ' × '

/**
 * 集計済みグループの「定義」（ブランド1のどのセグメント × ブランド2のどのセグメントか）をグループごとの文字列にして返します。
 *
 * カスタマイズ分析側と同様、編集中の groupInfos ではなく、
 * いま画面に出ている表を生成した `lastTabulatedVariables` を参照します。
 *
 * @returns グループ → 定義文字列。定義を持たないグループは欠落します。
 */
export const useGroupDefinitionMap = (): GroupDefinitionMap => {
  const { t } = useTranslation()
  const lastTabulatedVariables = useAtomValue(lastTabulatedVariablesAtom)
  const brandGroup = useSurveyQueryActiveBrandGroup()

  return React.useMemo<GroupDefinitionMap>(() => {
    const overlapGroupFilters =
      lastTabulatedVariables?.overlapGroupFilters ?? []
    const labelMap = createFiveSegmentWithAllLabelMap(t)
    const brandName = (id: number): string =>
      brandGroup?.brands.find((brand) => brand.id === id)?.name ?? ''

    // combinations は `LOYAL_x_GENERAL` 形式（overlapKeyMap の値 `_x_` 区切り）。
    const toCellText = (
      combination: string,
      brand1Name: string,
      brand2Name: string,
    ): string =>
      combination
        .split('_x_')
        .map((overlapKey, index) => {
          const fiveSegmentWithAll =
            invertedOverlapKeyMap[overlapKey as OverlapKey]
          const segmentLabel = labelMap[fiveSegmentWithAll] ?? overlapKey
          const name = index === 0 ? brand1Name : brand2Name

          return name === '' ? segmentLabel : `${name} ${segmentLabel}`
        })
        .join(BRAND_SEPARATOR)

    return R.mapToObj(overlapGroupFilters, (overlapGroupFilter) => {
      const brand1Name = brandName(overlapGroupFilter.brand1)
      const brand2Name = brandName(overlapGroupFilter.brand2)

      return [
        overlapGroupFilter.group as Group,
        overlapGroupFilter.combinations
          .map((combination) => toCellText(combination, brand1Name, brand2Name))
          .join(CELL_SEPARATOR),
      ]
    })
  }, [brandGroup?.brands, lastTabulatedVariables, t])
}
